import React, { Fragment, useState } from 'react'
import { Eye as EyeIcon, Info as InfoIcon } from 'react-feather'
import { useForm, Controller } from 'react-hook-form'
import {
  Button,
  Box,
  Grid,
  TextField,
  Typography,
  IconButton,
  Tooltip,
} from '@material-ui/core'
import { Alert, Skeleton } from '@material-ui/lab'

import { Card, LoadingFeedback, SwitchController, Select } from 'components'
import { LogEmailDialog } from './components'

import useSnackbar from 'hooks/useSnackbar'
import useFetch from 'hooks/useFetch'

import * as service from 'service'
import helpers from 'helpers'
import schema from './schema'
import constants from 'constants/index'
import theme from 'theme'

const Smtp = ({ smtpCredential, isLoading, refresh }) => {
  const snackbar = useSnackbar()
  const [loading, setLoading] = useState(false)
  const [openLogEmail, setOpenLogEmail] = useState(false)

  const defaultActive = smtpCredential?.active ?? true
  const defaultEnableStarttlsAuto = smtpCredential?.enableStarttlsAuto ?? true

  const { STATUS_BY_SEVERITY, ALERT_MESSAGE_BY_STATUS, CONFIGURED } =
    constants.smtp

  const severity = STATUS_BY_SEVERITY[smtpCredential?.status] || 'info'
  const message =
    ALERT_MESSAGE_BY_STATUS[smtpCredential?.status] ||
    'Adicione as credenciais SMTP para enviar e-mails.'
  const lastConfiguredAt =
    smtpCredential?.status === CONFIGURED
      ? helpers.formatters.date(smtpCredential?.lastConfiguredAt)
      : ''

  const viewPassword = () => {
    const input = document.querySelector('input[name="password"]')
    input.type = input.type === 'password' ? 'text' : 'password'
  }

  const { response, isLoading: isLoadingLog } = useFetch(
    service.dponet.smtp.mailLog,
    {},
    [],
  )

  const { control, handleSubmit, errors, watch } = useForm({
    validationSchema: schema,
    defaultValues: {
      userName: smtpCredential?.userName || '',
      password: smtpCredential?.password || '',
      address: smtpCredential?.address || '',
      port: smtpCredential?.port || 587,
      domain: smtpCredential?.domain || '',
      authentication: smtpCredential?.authentication || 'login',
      fromEmail: smtpCredential?.fromEmail || '',
      active: defaultActive,
      enableStarttlsAuto: defaultEnableStarttlsAuto,
    },
  })

  const onSubmit = async (data) => {
    setLoading(true)
    try {
      if (smtpCredential?.id) {
        await service.dponet.smtp.update({
          smtpCredentialId: smtpCredential.id,
          smtpCredential: data,
        })
      } else {
        await service.dponet.smtp.create({
          smtpCredential: data,
        })
      }
      snackbar.open({
        message:
          'Configuração do servidor de envio de e-mail salva com sucesso',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error.response.data.error) ||
          'Ocorreu um erro, tente novamente',
        variant: 'error',
      })
    } finally {
      setLoading(false)
      refresh()
    }
  }

  const handleTestConnection = async () => {
    setLoading(true)
    try {
      const response = await service.dponet.smtp.testConnection()
      const success = response?.data?.smtpCredential?.status === 'configured'

      snackbar.open({
        message: success
          ? 'Conexão com o servidor de e-mail testada com sucesso'
          : 'Falha ao testar a conexão com o servidor de e-mail',
        variant: success ? 'success' : 'error',
      })
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error.response.data.error) ||
          'Ocorreu um erro ao testar a conexão, tente novamente',
        variant: 'error',
      })
    } finally {
      setLoading(false)
      refresh()
    }
  }
  const handleLogsEmail = () => setOpenLogEmail(true)

  const handleInfo = (field) => {
    snackbar.open({
      message: constants.smtp.INFO_MESSAGES_BY_FIELD[field],
      variant: 'info',
    })
  }

  return (
    <>
      <Card
        title="Configuração do servidor de envio de e-mail (SMTP)"
        actionButton={
          <Button
            disabled={loading || isLoading}
            color="primary"
            variant="outlined"
            onClick={handleLogsEmail}
          >
            HISTÓRICO DE ENVIOS
          </Button>
        }
      >
        <LoadingFeedback open={loading} />
        <Box mb={2}>
          <Alert severity={severity} variant="outlined">
            <Typography variant="body2">
              {message} {lastConfiguredAt}
            </Typography>
          </Alert>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          gridGap={theme.spacing(2)}
          component="form"
          id="smtp-form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Grid container spacing={2}>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, index) => (
                <Grid key={index} item xs={12} sm={6}>
                  <Skeleton variant="rect" width="100%" height={54} />
                </Grid>
              ))
            ) : (
              <Fragment>
                <Grid item xs={12} sm={6}>
                  <Controller
                    as={
                      <TextField
                        label="Usuário da conta *"
                        type="text"
                        color="primary"
                        variant="outlined"
                        error={!!errors.userName}
                        helperText={errors?.userName?.message}
                        fullWidth
                        placeholder="user@proverdor.com"
                      />
                    }
                    control={control}
                    name="userName"
                    mode="onChange"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    as={
                      <TextField
                        label="Senha da conta *"
                        type="password"
                        color="primary"
                        variant="outlined"
                        error={!!errors.password}
                        helperText={errors?.password?.message}
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <IconButton
                              aria-label="Exibir senha"
                              size="small"
                              onClick={viewPassword}
                            >
                              <EyeIcon />
                            </IconButton>
                          ),
                        }}
                      />
                    }
                    control={control}
                    name="password"
                    mode="onChange"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    as={
                      <TextField
                        label="Endereço do servidor *"
                        type="text"
                        color="primary"
                        variant="outlined"
                        error={!!errors.address}
                        helperText={errors?.address?.message}
                        fullWidth
                        placeholder="smtp.gmail.com"
                      />
                    }
                    control={control}
                    name="address"
                    mode="onChange"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    as={
                      <TextField
                        label="Porta *"
                        type="number"
                        color="primary"
                        variant="outlined"
                        error={!!errors.port}
                        helperText={errors?.port?.message}
                        fullWidth
                      />
                    }
                    control={control}
                    name="port"
                    mode="onBlur"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    as={
                      <TextField
                        label="Domínio *"
                        type="text"
                        color="primary"
                        variant="outlined"
                        error={!!errors.domain}
                        helperText={errors?.domain?.message}
                        fullWidth
                        placeholder="gmail.com"
                        InputProps={{
                          startAdornment: (
                            <Box mr={1}>
                              <IconButton
                                aria-label="Informações sobre o domínio"
                                size="small"
                                onClick={() => handleInfo('domain')}
                              >
                                <InfoIcon />
                              </IconButton>
                            </Box>
                          ),
                        }}
                      />
                    }
                    control={control}
                    name="domain"
                    mode="onChange"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    as={
                      <Select
                        items={constants.smtp.AUTHENTICATION_TYPES}
                        label="Tipo de autenticação"
                        color="primary"
                        variant="outlined"
                        error={!!errors.authentication}
                        helperText={errors?.authentication?.message}
                        fullWidth
                      />
                    }
                    control={control}
                    name="authentication"
                    mode="onChange"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    as={
                      <TextField
                        label="E-mail de envio *"
                        type="email"
                        color="primary"
                        variant="outlined"
                        error={!!errors.fromEmail}
                        helperText={errors?.fromEmail?.message}
                        fullWidth
                        placeholder="no-reply@provedor.com"
                        InputProps={{
                          startAdornment: (
                            <Box mr={1}>
                              <IconButton
                                aria-label="Informações sobre o e-mail de envio"
                                size="small"
                                onClick={() => handleInfo('fromEmail')}
                              >
                                <InfoIcon />
                              </IconButton>
                            </Box>
                          ),
                        }}
                      />
                    }
                    control={control}
                    name="fromEmail"
                    mode="onChange"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">
                    Utilizar essa credencial como padrão
                  </Typography>
                  <SwitchController
                    name="active"
                    dynamicChecked={watch('active')}
                    control={control}
                    option={{
                      left: false,
                      right: true,
                    }}
                    labelOption={{
                      left: 'Não',
                      right: 'Sim',
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex">
                    <Typography variant="body1">
                      Usar STARTTLS (conexão segura automática)
                      <IconButton
                        aria-label="Informações sobre o STARTTLS"
                        size="small"
                        onClick={() => handleInfo('enableStarttlsAuto')}
                      >
                        <InfoIcon />
                      </IconButton>
                    </Typography>
                  </Box>
                  <SwitchController
                    name="enableStarttlsAuto"
                    dynamicChecked={watch('enableStarttlsAuto')}
                    control={control}
                    option={{
                      left: false,
                      right: true,
                    }}
                    labelOption={{
                      left: 'Não',
                      right: 'Sim',
                    }}
                  />
                </Grid>
              </Fragment>
            )}
          </Grid>
          <Box
            display="flex"
            justifyContent="flex-end"
            gridGap={theme.spacing(2)}
          >
            {smtpCredential?.id && (
              <Box>
                <Tooltip title="Somente possível testar a conexão com o servidor de e-mail após salvar as configurações.">
                  <Button
                    disabled={loading || isLoading}
                    color="primary"
                    variant="outlined"
                    onClick={handleTestConnection}
                  >
                    TESTAR CONEXÃO
                  </Button>
                </Tooltip>
              </Box>
            )}
            <Button
              disabled={loading || isLoading}
              color="primary"
              variant="contained"
              type="submit"
              form="smtp-form"
            >
              SALVAR
            </Button>
          </Box>
        </Box>
      </Card>
      {openLogEmail && !isLoadingLog && (
        <LogEmailDialog
          setOpen={setOpenLogEmail}
          open={openLogEmail}
          logs={response?.data?.mailLogs || []}
        />
      )}
    </>
  )
}

export default Smtp
